import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getSchoolNetwork,
  createSchoolNetwork,
  updateSchoolNetwork,
  getAvailableSchools,
  getAllAvailableSchools,
  updateNetworkSchools,
} from '@/api/school-networks';
import { createWhiteLabel, deleteWhiteLabel } from '@/api/white-labels';
import type { AvailableSchool } from '@/types/school-network';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiSelect } from '@/components/ui/multi-select';
import type { MultiSelectOption } from '@/components/ui/multi-select';
import { ArrowLeft, Network, Save, Loader2, School, Tag, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function SchoolNetworkFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [availableSchools, setAvailableSchools] = useState<AvailableSchool[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  // White label state
  const [whiteLabel, setWhiteLabel] = useState<{ id: number; name: string } | null>(null);
  const [whiteLabelName, setWhiteLabelName] = useState('');
  const [showDeleteWhiteLabel, setShowDeleteWhiteLabel] = useState(false);
  const [deletingWhiteLabel, setDeletingWhiteLabel] = useState(false);

  const schoolOptions: MultiSelectOption[] = useMemo(
    () =>
      availableSchools.map((school) => ({
        value: String(school.id),
        label: school.name,
        description: school.currentNetwork && !school.inNetwork
          ? `(${school.code}) — dans: ${school.currentNetwork.name}`
          : `(${school.code})`,
      })),
    [availableSchools]
  );

  const selectedSchoolValues = useMemo(
    () => selectedSchoolIds.map(String),
    [selectedSchoolIds]
  );

  const handleSchoolsChange = (values: string[]) => {
    setSelectedSchoolIds(values.map(Number));
  };

  useEffect(() => {
    if (isEdit) {
      fetchNetwork();
      fetchAvailableSchools();
    } else {
      fetchAllAvailableSchools();
    }
  }, [id, isEdit]);

  const fetchNetwork = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSchoolNetwork(parseInt(id!));
      setName(data.name);
      setDescription(data.description || '');
      setSelectedSchoolIds(data.schools.map((s) => s.id));
      if (data.whiteLabel) {
        setWhiteLabel(data.whiteLabel);
        setWhiteLabelName(data.whiteLabel.name);
      }
    } catch (err) {
      setError('Erreur lors du chargement du reseau');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSchools = async () => {
    try {
      setSchoolsLoading(true);
      const data = await getAvailableSchools(parseInt(id!));
      setAvailableSchools(data);
      setSelectedSchoolIds(data.filter((s) => s.inNetwork).map((s) => s.id));
    } catch (err) {
      console.error(err);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const fetchAllAvailableSchools = async () => {
    try {
      setSchoolsLoading(true);
      const data = await getAllAvailableSchools();
      setAvailableSchools(data);
    } catch (err) {
      console.error(err);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Le nom est requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let whiteLabelId: number | undefined | null = undefined;

      // Determine white label action
      const whiteLabelNameTrimmed = whiteLabelName.trim();
      if (whiteLabelNameTrimmed && !whiteLabel) {
        // Create new white label with auto-generated host
        const hostSlug = whiteLabelNameTrimmed
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') // Remove accents
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        const wlResult = await createWhiteLabel({
          name: whiteLabelNameTrimmed,
          host: `${hostSlug}.ecolefutee.com`,
        });
        whiteLabelId = wlResult.id;
      } else if (whiteLabel && whiteLabelNameTrimmed !== whiteLabel.name) {
        // Name changed - for now we don't update name, only association
        whiteLabelId = whiteLabel.id;
      } else if (whiteLabel) {
        whiteLabelId = whiteLabel.id;
      } else if (!whiteLabelNameTrimmed && whiteLabel) {
        // Clearing white label
        whiteLabelId = null;
      }

      if (isEdit) {
        await updateSchoolNetwork(parseInt(id!), {
          name: name.trim(),
          description: description.trim() || null,
          whiteLabelId: whiteLabelId,
        });
        await updateNetworkSchools(parseInt(id!), selectedSchoolIds);
        navigate(`/school-networks/${id}`);
      } else {
        const result = await createSchoolNetwork({
          name: name.trim(),
          description: description.trim() || undefined,
          whiteLabelId: whiteLabelId ?? undefined,
        });
        // Update schools for the new network
        if (selectedSchoolIds.length > 0) {
          await updateNetworkSchools(result.id, selectedSchoolIds);
        }
        navigate(`/school-networks/${result.id}`);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          (err instanceof Error ? err.message : 'Erreur lors de la sauvegarde du reseau')
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWhiteLabel = async () => {
    if (!whiteLabel) return;

    try {
      setDeletingWhiteLabel(true);
      // First dissociate from network
      await updateSchoolNetwork(parseInt(id!), {
        whiteLabelId: null,
      });
      // Then delete the white label
      await deleteWhiteLabel(whiteLabel.id);
      setWhiteLabel(null);
      setWhiteLabelName('');
      setShowDeleteWhiteLabel(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erreur lors de la suppression de la marque blanche');
    } finally {
      setDeletingWhiteLabel(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to={isEdit ? `/school-networks/${id}` : '/school-networks'}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Network className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            {isEdit ? 'Modifier le reseau' : 'Nouveau reseau'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? 'Modifiez les informations du reseau'
              : 'Créez un nouveau réseau pour regrouper vos écoles'}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Network info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Nom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Reseau Paris Est"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description optionnelle du reseau..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* White Label */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Marque blanche
              </CardTitle>
              <CardDescription>
                Associez une marque blanche a ce reseau (optionnel)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {whiteLabel ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                    <div>
                      <p className="font-medium">{whiteLabel.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Marque blanche associee
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setShowDeleteWhiteLabel(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="whiteLabelName">Nom de la marque blanche</Label>
                  <Input
                    id="whiteLabelName"
                    value={whiteLabelName}
                    onChange={(e) => setWhiteLabelName(e.target.value)}
                    placeholder="Ex: Ma Marque Blanche"
                  />
                  <p className="text-xs text-muted-foreground">
                    Laissez vide si vous ne souhaitez pas associer de marque blanche
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Schools selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <School className="h-4 w-4" />
                Écoles du réseau ({selectedSchoolIds.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {schoolsLoading ? (
                <Skeleton className="h-12" />
              ) : (
                <MultiSelect
                  options={schoolOptions}
                  selected={selectedSchoolValues}
                  onChange={handleSchoolsChange}
                  placeholder="Sélectionner des écoles..."
                  searchPlaceholder="Rechercher une école..."
                  emptyMessage="Aucune école trouvée"
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(isEdit ? `/school-networks/${id}` : '/school-networks')
            }
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEdit ? 'Enregistrer' : 'Creer le reseau'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Delete white label confirmation */}
      <AlertDialog open={showDeleteWhiteLabel} onOpenChange={setShowDeleteWhiteLabel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la marque blanche ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer la marque blanche{' '}
              <strong>{whiteLabel?.name}</strong> ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingWhiteLabel}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWhiteLabel}
              disabled={deletingWhiteLabel}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deletingWhiteLabel ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
